import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import {
  Camera,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Scan,
  AlertTriangle,
  RefreshCw,
  LogOut,
  User,
  Ticket
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../Constants';
import { formatDate } from "../shared/DateField";
import AppContext from '../../AppContext';

const EventScannerPage = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isVirtual } = useContext(AppContext);
  const [event, setEvent] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState('gate-scanner-' + Math.random().toString(36).substring(7));
  const [selectedGate, setSelectedGate] = useState(null);
  const [hasQrCheckIn, setHasQrCheckIn] = useState(true);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);
  const scannerRef = useRef(null);

  useEffect(() => {
    loadEventData();
    loadStatistics();
    
    // Refresh statistics every 10 seconds
    const interval = setInterval(loadStatistics, 10000);
    
    // Cleanup function
    return () => {
      clearInterval(interval);
    };
  }, [eventId]);

  // Separate cleanup effect for scanner
  useEffect(() => {
    return () => {
      if (scanner && scanning) {
        scanner.stop().catch(err => {
          // Silently handle error on unmount
          console.log('Scanner cleanup skipped - not running');
        });
      }
    };
  }, [scanner, scanning]);

  const loadEventData = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Event/${eventId}`);
      setEvent(response.data);
      setLoading(false);
      loadEntitlements(response.data);
    } catch (err) {
      console.error('Error loading event:', err);
      setError('Failed to load event details');
      setLoading(false);
      setEntitlementsLoading(false);
    }
  };

  // Mirrors EventRegistrationsPage.jsx's entitlement resolution: legacy/non-virtual companies
  // are grandfathered in (no entitlement lookup applies to them); virtual companies match by the
  // event's own entitlementId, falling back to the first active entitlement.
  const loadEntitlements = async (eventData) => {
    try {
      if (!isVirtual) {
        setHasQrCheckIn(true);
        return;
      }
      const entitlementsRes = await axios.get(`${BASE_URL}/Company/my-entitlements`);
      const entitlements = entitlementsRes.data || [];
      const eventEntitlement = eventData?.entitlementId
        ? entitlements.find((e) => e.id === eventData.entitlementId)
        : null;
      const relevantEntitlement = eventEntitlement || entitlements.find((e) => e.status === "active");
      setHasQrCheckIn(!!relevantEntitlement?.hasQrCheckIn);
    } catch (err) {
      console.error('Error fetching entitlements:', err);
      setHasQrCheckIn(false);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Event/${eventId}/live-stats`);
      setStatistics(response.data);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const startScanning = async () => {
    // Check if gate selection is required
    if (event?.gates && event.gates.length > 0 && !selectedGate) {
      alert('Please select a gate before starting the scanner');
      return;
    }

    try {
      setScanning(true); // Set scanning to true first to hide overlay
      
      const html5QrCode = new Html5Qrcode("qr-reader");
      setScanner(html5QrCode);

      console.log('Starting camera...');
      console.log('Requesting camera permissions...');
      
      // Try to get camera devices first
      const devices = await Html5Qrcode.getCameras();
      console.log('Available cameras:', devices);
      
      if (!devices || devices.length === 0) {
        throw new Error('No camera devices found');
      }

      // Start with the first available camera (usually back camera on mobile)
      const cameraId = devices[devices.length - 1].id; // Last camera is usually back camera
      
      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          // Function form so the box shrinks to fit narrow phone viewfinders (a fixed 250px box
          // throws when it is larger than the camera view).
          qrbox: (w, h) => { const side = Math.floor(Math.min(250, Math.min(w, h) * 0.7)); return { width: side, height: side }; },
          aspectRatio: 1.0
        },
        onScanSuccess,
        onScanError
      );

      console.log('Camera started successfully');
    } catch (err) {
      console.error("Error starting scanner:", err);
      setScanning(false);
      setScanner(null);
      
      // More specific error messages
      if (err.name === 'NotAllowedError') {
        alert("Camera access denied. Please grant camera permissions and try again.");
      } else if (err.name === 'NotFoundError' || err.message?.includes('No camera')) {
        alert("No camera found. Please check if your device has a camera.");
      } else if (err.name === 'NotReadableError') {
        alert("Camera is already in use by another application.");
      } else if (err.name === 'OverconstrainedError') {
        alert("Camera doesn't support the requested configuration.");
      } else {
        alert(`Failed to start camera: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const stopScanning = async () => {
    if (scanner && scanning) {
      try {
        await scanner.stop();
        await scanner.clear();
      } catch (err) {
        // Ignore "not running" errors
        if (!err.message?.includes('not running')) {
          console.error("Error stopping scanner:", err);
        }
      } finally {
        // Always reset state
        setScanning(false);
        setScanner(null);
      }
    } else {
      // Just reset state if scanner isn't active
      setScanning(false);
      setScanner(null);
    }
  };

  const onScanSuccess = async (decodedText) => {
    // Pause scanning temporarily to prevent multiple scans
    if (scanner && scanning) {
      try {
        await scanner.pause();
      } catch (err) {
        console.error("Error pausing scanner:", err);
      }
    }

    // Extract token from URL or use directly
    let token = decodedText;
    if (decodedText.includes('token=')) {
      const urlParams = new URLSearchParams(decodedText.split('?')[1]);
      token = urlParams.get('token');
    }

    await validateAndCheckIn(token);
    
    // Resume scanning after 3 seconds
    setTimeout(() => {
      if (scanner && scanning) {
        try {
          scanner.resume();
        } catch (err) {
          console.error("Error resuming scanner:", err);
        }
      }
    }, 3000);
  };

  const onScanError = (errorMessage) => {
    // Ignore frequent scan errors
  };

  const validateAndCheckIn = async (qrToken) => {
    try {
      // Step 1: Validate the pass
      const validateResponse = await axios.post(
        `${BASE_URL}/EventRegistration/validate`,
        {
          qrCodeContent: qrToken,
          eventId: eventId,
          deviceId: deviceId
        }
      );

      const validation = validateResponse.data;

      if (!validation.isValid) {
        setLastScan({
          success: false,
          message: validation.message,
          reason: validation.invalidReason,
          timestamp: new Date()
        });
        playSound('error');
        return;
      }

      // Step 2: Check in the guest
      const checkInResponse = await axios.post(
        `${BASE_URL}/EventRegistration/check-in`,
        {
          registrationId: validation.registration.id,
          eventId: eventId,
          scannerUserId: localStorage.getItem('userId'),
          scannerDeviceId: deviceId,
          gateId: selectedGate?.gateId,
          gateName: selectedGate?.gateName,
          gateType: selectedGate?.gateType,
          checkInGate: selectedGate?.gateName || 'Main Entrance'
        }
      );

      const checkIn = checkInResponse.data;

      setLastScan({
        success: true,
        registration: validation.registration,
        checkIn: checkIn,
        isDuplicate: checkIn.isDuplicate,
        timestamp: new Date()
      });

      playSound(checkIn.isDuplicate ? 'warning' : 'success');
      loadStatistics(); // Refresh stats

    } catch (err) {
      console.error('Check-in error:', err);
      setLastScan({
        success: false,
        message: err.response?.data?.message || 'Failed to process check-in',
        reason: 'error',
        timestamp: new Date()
      });
      playSound('error');
    }
  };

  const playSound = (type) => {
    // Play audio feedback (you can add actual audio files)
    const audio = new Audio();
    if (type === 'success') {
      // audio.src = '/sounds/success.mp3';
      console.log('✓ SUCCESS BEEP');
    } else if (type === 'warning') {
      // audio.src = '/sounds/warning.mp3';
      console.log('⚠ WARNING BEEP');
    } else {
      // audio.src = '/sounds/error.mp3';
      console.log('✗ ERROR BEEP');
    }
    // audio.play();
  };

  const handleLogout = () => {
    if (scanning) {
      stopScanning();
    }
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (loading || entitlementsLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading scanner...</p>
        </div>
      </div>
    );
  }

  if (!hasQrCheckIn) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">{t('eventScanner.qrCheckInNotIncluded')}</h2>
          <button
            onClick={() => navigate('/events')}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Error</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/events')}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Scan className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">{event?.title}</h1>
                <p className="text-sm text-gray-600">Gate Scanner</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Gate Selection */}
        {event?.gates && event.gates.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Select Gate</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {event.gates
                .filter(gate => gate.isActive)
                .map((gate) => (
                  <button
                    key={gate.gateId}
                    onClick={() => setSelectedGate(gate)}
                    className={`p-4 rounded-lg border-2 transition ${
                      selectedGate?.gateId === gate.gateId
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-gray-800">{gate.gateName}</h3>
                      {selectedGate?.gateId === gate.gateId && (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">
                      Type: <span className="font-medium capitalize">{gate.gateType}</span>
                    </p>
                    {gate.location && (
                      <p className="text-sm text-gray-500">{gate.location}</p>
                    )}
                  </button>
                ))}
            </div>
            {!selectedGate && (
              <p className="text-orange-600 text-sm mt-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Please select a gate before scanning
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Scanner Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">QR Code Scanner</h2>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  scanning ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {scanning ? '● Scanning' : '○ Idle'}
                </div>
              </div>

              {/* Scanner View */}
              <div className="relative">
                <div 
                  id="qr-reader" 
                  className="rounded-lg overflow-hidden"
                  style={{ 
                    width: '100%',
                    minHeight: '400px'
                  }}
                />
                
                {!scanning && (
                  <div className="absolute inset-0 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center flex flex-col items-center justify-center">
                    <Camera className="w-24 h-24 text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">Ready to scan QR codes</p>
                    <button
                      onClick={startScanning}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Start Scanning
                    </button>
                  </div>
                )}
              </div>

              {scanning && (
                <button
                  onClick={stopScanning}
                  className="w-full mt-4 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition"
                >
                  Stop Scanning
                </button>
              )}

              {/* Last Scan Result */}
              {lastScan && (
                <div className={`mt-6 rounded-lg p-4 border-2 ${
                  lastScan.success 
                    ? lastScan.isDuplicate
                      ? 'bg-yellow-50 border-yellow-300'
                      : 'bg-green-50 border-green-300'
                    : 'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-start gap-3">
                    {lastScan.success ? (
                      lastScan.isDuplicate ? (
                        <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                      ) : (
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      )
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    )}
                    
                    <div className="flex-1">
                      {lastScan.success ? (
                        <>
                          <h3 className={`font-bold mb-2 ${
                            lastScan.isDuplicate ? 'text-yellow-900' : 'text-green-900'
                          }`}>
                            {lastScan.isDuplicate ? 'Already Checked In' : 'Check-In Successful'}
                          </h3>
                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-gray-800">
                              {lastScan.registration.firstName} {lastScan.registration.lastName}
                            </p>
                            <p className="text-gray-600">{lastScan.registration.email}</p>
                            <p className="text-gray-600">
                              Pass: {lastScan.registration.passSerialNumber}
                            </p>
                            <p className="text-gray-600 capitalize">
                              Type: {lastScan.registration.registrationType}
                            </p>
                            {lastScan.isDuplicate && (
                              <p className="text-yellow-700 mt-2">
                                Previous check-in: {new Date(lastScan.checkIn.firstCheckInAt).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="font-bold text-red-900 mb-2">Invalid Pass</h3>
                          <p className="text-sm text-red-700">{lastScan.message}</p>
                          {lastScan.reason && (
                            <p className="text-xs text-red-600 mt-1 capitalize">
                              Reason: {lastScan.reason}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Statistics Section */}
          <div className="space-y-6">
            {/* Live Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Live Statistics</h2>
                <button
                  onClick={loadStatistics}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <RefreshCw className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {statistics && (
                <div className="space-y-4">
                  {/* Current Gate Info */}
                  {selectedGate && (
                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-4 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm opacity-90">Current Gate</span>
                        <Scan className="w-5 h-5 opacity-75" />
                      </div>
                      <p className="text-xl font-bold mb-1">{selectedGate.gateName}</p>
                      <p className="text-xs opacity-75 capitalize">{selectedGate.gateType}</p>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm opacity-90">Total Registered</span>
                      <Users className="w-5 h-5 opacity-75" />
                    </div>
                    <p className="text-3xl font-bold">{statistics.totalRegistrations || 0}</p>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm opacity-90">Checked In</span>
                      <CheckCircle className="w-5 h-5 opacity-75" />
                    </div>
                    <p className="text-3xl font-bold">{statistics.totalCheckIns || 0}</p>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm opacity-90">Attendance Rate</span>
                      <Ticket className="w-5 h-5 opacity-75" />
                    </div>
                    <p className="text-3xl font-bold">
                      {statistics.totalRegistrations > 0
                        ? Math.round((statistics.totalCheckIns / statistics.totalRegistrations) * 100)
                        : 0}%
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-2">Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">VIP</span>
                        <span className="font-medium">{statistics.vipRegistrations || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Regular</span>
                        <span className="font-medium">{statistics.regularRegistrations || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 text-center">
                    <Clock className="w-3 h-3 inline mr-1" />
                    Updated {new Date().toLocaleTimeString()}
                  </div>
                </div>
              )}
            </div>

            {/* Event Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-gray-800 mb-3">Event Details</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(String(event.startDate).slice(0, 10), 'en', event.calendarPreference, event.hijriAdjustmentDays || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium text-gray-800">{event.location}</p>
                </div>
                <div>
                  <p className="text-gray-500">Capacity</p>
                  <p className="font-medium text-gray-800">{event.capacity} guests</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventScannerPage;
